import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpServiceBase } from '../classes/HttpServiceBase';

/**
 * Talks to the FYES gateway endpoints on our backend (rest/fyes/gateways).
 * The interceptor prepends environment.serverUrl, so baseUrl '/gateways'
 * resolves to <serverUrl>/gateways/...
 */
@Injectable({ providedIn: 'root' })
export class AuthService extends HttpServiceBase {
    protected override baseUrl = '/gateways';

    constructor(private http: HttpClient) {
        super();
        this.assign(this);
    }

    /** POST /gateways/login -> cloud /installation_check_user */
    public login(username: string, password: string): Observable<any> {
        return this._http.post(this.baseUrl + '/login', { username, password })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** POST /gateways/register -> cloud /installation_new_account */
    public register(email: string, password: string, activationCode: string): Observable<any> {
        return this._http.post(this.baseUrl + '/register', { email, password, activationCode })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /**
     * POST /gateways/activate-account -> cloud /user/check_activation_code.
     * `code` comes from the activation link in the cloud's registration email.
     */
    public activateAccount(code: string): Observable<any> {
        return this._http.post(this.baseUrl + '/activate-account', { code })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** GET /gateways -> cloud gw_list. idUser is derived from the session token. */
    public getGateways(): Observable<any> {
        return this._http.get(this.baseUrl)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** GET /gateways/all -> cloud gw_list with id_user = -1 (admin: every gateway). */
    public getAllGateways(): Observable<any> {
        return this._http.get(this.baseUrl + '/all')
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** POST /gateways/provision -> create the local code<->uid mapping (admin only). */
    public provisionGateway(uid: string): Observable<any> {
        return this._http.post(this.baseUrl + '/provision', { uid })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /**
     * PUT /gateways/:uid/meta -> save our own fields for a gateway (admin only).
     * The cloud is not touched. Only the keys passed are changed; null clears.
     */
    public saveGatewayMeta(uid: string, meta: { customerName?: string | null; notes?: string | null; nickname?: string | null; activationDate?: string }): Observable<any> {
        return this._http.put(`${this.baseUrl}/${encodeURIComponent(uid)}/meta`, meta)
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** The unissued activation code pool (admin). Returns { codes, count,
     *  truncated } — the caller turns it into the downloadable file. */
    public getActivationCodes(): Observable<any> {
        return this._http.get(this.baseUrl + '/activation-codes')
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** Customer-facing rename. Separate from saveGatewayMeta, which is admin
     *  only; this one is session-scoped and checks the gateway is yours. */
    public saveGatewayNickname(uid: string, nickname: string | null): Observable<any> {
        return this._http.put(`${this.baseUrl}/${encodeURIComponent(uid)}/nickname`, { nickname })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** POST /gateways/set-active -> activate/deactivate a gateway (admin only). */
    public setGatewayActive(uid: string, value: boolean): Observable<any> {
        return this._http.post(this.baseUrl + '/set-active', { uid, value })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** DELETE /gateways -> Empty: cloud unbind + clear activation date (admin). */
    public emptyGateway(uid: string): Observable<any> {
        return this._http.delete(this.baseUrl, { body: { uid } })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** DELETE /gateways/permanent -> Delete: cloud unbind + soft-delete (admin). */
    public deleteGateway(uid: string): Observable<any> {
        return this._http.delete(this.baseUrl + '/permanent', { body: { uid } })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** POST /gateways/change-username -> change the customer's username (admin). */
    public changeUsername(idUser: string | number, newUsername: string): Observable<any> {
        return this._http.post(this.baseUrl + '/change-username', { idUser, newUsername })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** POST /gateways/change-password -> change the customer's password (admin). */
    public changePassword(idUser: string | number, newPassword: string): Observable<any> {
        return this._http.post(this.baseUrl + '/change-password', { idUser, newPassword })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /** POST /gateways/replace -> swap a faulty gateway for a new one (admin). */
    public replaceGateway(oldUid: string, newUid: string, newCode: string): Observable<any> {
        return this._http.post(this.baseUrl + '/replace', { oldUid, newUid, newCode })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }

    /**
     * POST /gateways/activate -> cloud /installation_activate_gateway.
     * Binds an additional gateway to the logged-in user (idUser from the token).
     */
    public activateGateway(activationCode: string): Observable<any> {
        return this._http.post(this.baseUrl + '/activate', { activationCode })
            .pipe(map((r: any) => r), catchError(this.handleError.bind(this)));
    }
}
